import { useContext, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { inject, observer, PropTypes as MobXPropTypes } from 'mobx-react';
import Chat from '../Chat';
import NewTopicForm from '../NewTopicForm';
import NoMessagesPlug from '../NoMessagesPlug';
import Topics from '../Topics';
import i18nContext from 'components/i18n-context';
import { MESSAGE_TOPIC_PERMISSIONS } from 'components/constants';
import { hasRole, MESSAGES_CREATE } from 'services/roles';
import Loader from 'components/Loader';
import { ROUTE_PATHS } from 'routes/constants';
import { NewContainer } from 'uikit/NewContainer/NewContainer';
import './MessagesDashboard.scss';

const MessagesDashboard = ({ messagesStore }) => {
  const i18n = useContext(i18nContext);
  const location = useLocation();
  const navigate = useNavigate();
  const { id: topicId } = useParams();
  const topicsRef = useRef(null);
  const [isAnnouncementTopic, setIsAnnouncementTopic] = useState(false);

  const clearError = () => messagesStore.clearError();
  const setIsNewTopicCreatedStatus = (status) => messagesStore.setIsNewTopicCreated(status);
  const setIsNewMessageCreated = (status) => messagesStore.setIsNewMessageCreated(status);
  const setNewTopicData = (subject, message) => messagesStore.setNewTopicData(subject, message);
  const setNewMessageData = (message) => messagesStore.setNewMessageData(message);
  const clearUploadedFiles = () => messagesStore.clearUploadedFiles();
  const removeAttachment = (fileId) => messagesStore.removeAttachment(fileId);
  const downloadAttachment = async (fileId) => {
    await messagesStore.downloadTopicDocument(fileId);
  };

  const getCurrentTopicMessagesFilesUrls = async () => {
    await messagesStore.getCurrentTopicMessagesFilesUrls();
  };
  const createNewTopic = async (topicData) => {
    try {
      await messagesStore.createNewTopic('private', topicData);
      navigate(ROUTE_PATHS.MESSAGES);
    } catch (error) {
      // Error handling is already done in messagesStore
      console.error('Failed to create new topic:', error);
    }
  };
  const createNewMessage = async (topicId) => {
    await messagesStore.sendTopicMessage(topicId, {
      messageBody: messagesStore.newMessageData,
      uploadedFiles: messagesStore.uploadedFiles
    });
  };

  const updateTopicOwnerOperator = async (topicId, ownerOperatorId) => {
    await messagesStore.updateTopicOwnerOperator(topicId, ownerOperatorId);
  };

  const updateTopicStatus = async (topicId, status) => {
    await messagesStore.updateTopicStatus(topicId, status);
  };

  const handleClickNewMessageButton = () => {
    navigate(ROUTE_PATHS.NEW_MESSAGE);
  };

  const handleChangeActiveTopic = async (topicId) => {
    navigate(`${ROUTE_PATHS.MESSAGES}/${topicId}`);
  };

  useEffect(() => {
    if (messagesStore.isNewTopicCreated && messagesStore.topics.length > 0) {
      handleChangeActiveTopic(messagesStore.topics[0]?.id);
      setIsNewTopicCreatedStatus(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messagesStore.isNewTopicCreated, messagesStore.topics]);

  useEffect(() => {
    if (messagesStore.topics.length > 0 && topicId) {
      const topic = messagesStore.topics.find((t) => t.id === topicId);
      if (topic?.permission === MESSAGE_TOPIC_PERMISSIONS.ANNOUNCEMENT) {
        setIsAnnouncementTopic(true);
      } else {
        setIsAnnouncementTopic(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messagesStore.topics, topicId]);

  useEffect(() => {
    messagesStore.resetForms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const isNoMessage = !messagesStore.hasAnyTopics && !messagesStore.isLoading;
  // Show chat if we have topicId in URL, even if data is still loading
  const isChat = !!topicId && location.pathname.includes('/messages/') && location.pathname !== ROUTE_PATHS.NEW_MESSAGE;
  const isCreateNewTopicForm = location.pathname === ROUTE_PATHS.NEW_MESSAGE;
  // Check if topic data is still loading
  const isTopicDataLoading = isChat && (messagesStore.isTopicLoading || !messagesStore.currentTopicInfo);

  const showWriteNewMessage = hasRole(MESSAGES_CREATE);

  // Определяем заголовок на основе текущего маршрута
  const getHeaderText = () => {
    if (isCreateNewTopicForm) {
      return i18n.getMessage('container.newMessage') || 'New message';
    } else if (isChat && topicId) {
      // Use currentTopicInfo from store which is always fresh from server
      const subject = messagesStore.currentTopicInfo?.subject || '';

      if (subject) {
        return subject;
      } else {
        return i18n.getMessage('container.messages') || 'Messages';
      }
    } else {
      return i18n.getMessage('container.messages') || 'Messages';
    }
  };

  // Get current topic from store - this is always fresh from server
  const currentTopic = isChat && topicId ? messagesStore.currentTopicInfo : null;

  return (
    <NewContainer
      className={`${isCreateNewTopicForm ? 'new-topic-container' : isChat ? 'chat-main-container' : 'messages-container'} content-card-wrapper`}
      header={getHeaderText()}
      isBackButton={location.pathname !== ROUTE_PATHS.MESSAGES}
      isMaxWidthUnset={true}
    >
      {!messagesStore.isInitialized ? (
        <div className={'loader-container'}>
          <Loader />
        </div>
      ) : (
        <div className={'messages-wrapper'}>
          {isCreateNewTopicForm ? (
            <NewTopicForm
              newTopicData={messagesStore.newTopic}
              setNewTopicData={setNewTopicData}
              isLoading={messagesStore.isLoading || messagesStore.isFileUploading}
              error={messagesStore.error}
              clearError={clearError}
              uploadedFiles={messagesStore.uploadedFiles}
              uploadDocuments={messagesStore.uploadAttachments}
              removeAttachment={removeAttachment}
              onSubmit={createNewTopic}
            />
          ) : isChat ? (
            isTopicDataLoading ? (
              <div className={'loader-container'}>
                <Loader />
              </div>
            ) : (
              <Chat
                topic={currentTopic}
                messages={messagesStore.currentTopicMessages}
                isLoading={messagesStore.isTopicMessagesLoading}
                isFileUploading={messagesStore.isFileUploading}
                error={messagesStore.error}
                clearError={clearError}
                setNewMessageData={setNewMessageData}
                uploadedFiles={messagesStore.uploadedFiles}
                uploadDocuments={messagesStore.uploadAttachments}
                removeAttachment={removeAttachment}
                downloadAttachment={downloadAttachment}
                topicsRef={topicsRef}
                canWriteMessages={showWriteNewMessage && !isAnnouncementTopic}
                clearUploadedFiles={clearUploadedFiles}
                isNewMessageCreated={messagesStore.isNewMessageCreated}
                setIsNewMessageCreated={setIsNewMessageCreated}
                onSendMessage={(topicId) => createNewMessage(topicId)}
                updateTopicOwnerOperator={updateTopicOwnerOperator}
                updateTopicStatus={updateTopicStatus}
                getCurrentTopicMessagesFilesUrls={getCurrentTopicMessagesFilesUrls}
                headerText={getHeaderText()}
                messagesStore={messagesStore}
              />
            )
          ) : isNoMessage ? (
            <NoMessagesPlug
              handleClickNewMessageButton={handleClickNewMessageButton}
              showWriteNewMessage={showWriteNewMessage}
            />
          ) : (
            <Topics
              topics={messagesStore.topics}
              activeTopicIndex={messagesStore.currentTopicIndex}
              handleChangeActiveTopic={handleChangeActiveTopic}
              handleClickNewMessageButton={handleClickNewMessageButton}
              topicsRef={topicsRef}
              showWriteMessageButton={showWriteNewMessage}
            />
          )}
        </div>
      )}
    </NewContainer>
  );
};

MessagesDashboard.propTypes = {
  messagesStore: MobXPropTypes.observableObject
};

export default inject((stores) => ({
  messagesStore: stores.messagesStore
}))(observer(MessagesDashboard));
